import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminCursoService } from '../../shared/services';
import type { Curso } from '../../shared/types';
import { 
  BookOpen, 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  CheckCircle, 
  XCircle,
  Filter
} from 'lucide-react';
import { Link } from 'react-router-dom';

export function AdminCursosPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [ativoFilter, setAtivoFilter] = useState<string>('');
  const [categoriaFilter, setCategoriaFilter] = useState<string>('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-cursos', search, ativoFilter, categoriaFilter],
    queryFn: () => adminCursoService.getCursos({ 
      search: search || undefined, 
      ativo: ativoFilter === '' ? undefined : ativoFilter === 'true',
      categoria: categoriaFilter || undefined,
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (cursoId: string) => adminCursoService.deleteCurso(cursoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-cursos'] });
    },
  });

  const handleDelete = (cursoId: string, cursoTitulo: string) => {
    if (window.confirm(`Tem certeza que deseja excluir o curso "${cursoTitulo}"?`)) {
      deleteMutation.mutate(cursoId);
    }
  };

  const categorias = ['Excel', 'Power BI', 'Soft Skills', 'Gestão', 'Tecnologia'];

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-blue-600" />
              Gerenciar Cursos
            </h1>
            <p className="text-gray-600 mt-2">
              Crie e gerencie cursos da plataforma
            </p>
          </div>
          <Link
            to="/admin/cursos/novo"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Novo Curso
          </Link>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="font-medium text-gray-900">Filtros</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar cursos..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={categoriaFilter}
              onChange={(e) => setCategoriaFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todas as categorias</option>
              {categorias.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <select
              value={ativoFilter}
              onChange={(e) => setAtivoFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos os status</option>
              <option value="true">Ativos</option>
              <option value="false">Inativos</option>
            </select>
          </div>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Cursos</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{data?.length || 0}</p>
              </div>
              <BookOpen className="w-10 h-10 text-blue-600 opacity-50" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Cursos Ativos</p>
                <p className="text-2xl font-bold text-green-900 mt-1">
                  {data?.filter((c: Curso) => c.ativo).length || 0}
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-600 opacity-50" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Cursos Inativos</p>
                <p className="text-2xl font-bold text-red-900 mt-1">
                  {data?.filter((c: Curso) => !c.ativo).length || 0}
                </p>
              </div>
              <XCircle className="w-10 h-10 text-red-600 opacity-50" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Categorias</p>
                <p className="text-2xl font-bold text-purple-900 mt-1">{categorias.length}</p>
              </div>
              <Filter className="w-10 h-10 text-purple-600 opacity-50" />
            </div>
          </div>
        </div>

        {/* Grid de Cursos */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Carregando cursos...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                {data?.map((curso: Curso) => (
                  <div
                    key={curso.id}
                    className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <img
                      src={curso.capaUrl}
                      alt={curso.titulo}
                      className="w-full h-40 object-cover"
                    />
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-bold text-gray-900 line-clamp-2 flex-1">
                          {curso.titulo}
                        </h3>
                        <div className="flex gap-1 ml-2">
                          <Link
                            to={`/admin/cursos/${curso.id}/editar`}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Editar"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={() => handleDelete(curso.id, curso.titulo)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                        {curso.descricao}
                      </p>

                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                          {curso.categoria}
                        </span>
                        {curso.ativo ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Ativo
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium flex items-center gap-1">
                            <XCircle className="w-3 h-3" />
                            Inativo
                          </span>
                        )}
                      </div>

                      <div className="text-xs text-gray-500 space-y-1">
                        {curso.duracao && <p>Duração: {curso.duracao}</p>}
                        {curso.instrutor && <p>Instrutor: {curso.instrutor}</p>}
                        <p>Slug: {curso.slug}</p>
                      </div>
                    </div>
                  </div>
                ))}

                {data && data.length === 0 && (
                  <div className="col-span-full text-center py-12">
                    <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">Nenhum curso encontrado</p>
                  </div>
                )}
              </div>

              {/* Paginação - Comentada pois a API retorna array direto */}
              {/*data && data.totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Mostrando {((page - 1) * data.pageSize) + 1} até {Math.min(page * data.pageSize, data.total)} de {data.total} cursos
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => setPage(p => Math.min(data.totalPages, p + 1))}
                      disabled={page === data.totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Próxima
                    </button>
                  </div>
                </div>
              )*/}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
